// src/components/seo/EnhancedProductsJsonLd.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BranchProduct } from '../../types';

interface EnhancedProductsJsonLdProps {
  products: BranchProduct[];
}

const EnhancedProductsJsonLd: React.FC<EnhancedProductsJsonLdProps> = ({ products }) => {
  if (!products || products.length === 0) return null;

  // الحصول على النطاق الأساسي
  const getOrigin = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'https://bazaar-marketplace.com'; // استبدل بالنطاق الفعلي
  };

  const origin = getOrigin();

  const generateProductSchema = (product: BranchProduct) => {
    // بيانات المتجر الفعلية
    const storeName = product.branch?.store?.name || "3M Store";
    const storeSlug = product.branch?.store?.slug || "3m";
    const storePhone = "+201503323006"; // رقم افتراضي
    const storeEmail = "info@3m.ma"; // إيميلك الفعلي
    const storeWebsite = `${origin}/store/${storeSlug}`;
    
    // صور المتجر (إذا كان لديك)
    const storeLogo = product.branch?.store?.theme?.logo || `${origin}/logo.png`;
    const storeImage = product.branch?.store?.theme?.background_image_desktop || `${origin}/images/store-default.jpg`;
    
    // صور المنتج
    const productImages = product.product.images?.map(img => img.image) || 
                         [`${origin}/images/default-product.jpg`];
    
    // التأكد من وجود صورة واحدة على الأقل
    const mainImage = productImages.length > 0 ? productImages[0] : `${origin}/images/default-product.jpg`;
    
    // GTIN - أزلته لأنه ليس ضرورياً
    // const gtin = product.product.barcode ? `GTIN${product.product.barcode}` : undefined;
    
    // وصف المنتج
    const isValidDescription = (text: string | undefined) =>
  typeof text === "string" && text.trim().length >= 100;

const productDescription =
  (isValidDescription(product?.description) && product.description) ||
  (isValidDescription(product?.descriptionAR) && product.descriptionAR) ||
  (isValidDescription(product.product?.description) &&
    product.product.description.substring(0, 200)) ||
  `${product.product.name} - متوفر الآن في متجر ${storeName} بسعر ${product.price} جنيه مصري. منتج عالي الجودة مع ضمان واسترجاع مجاني.`;

    
    // تحديد حالة التوفر
    const availability = product.stock > 0 ? 
      "https://schema.org/InStock" : 
      "https://schema.org/OutOfStock";
    
    // سياسة الإرجاع
    const returnPolicy = {
      "@type": "MerchantReturnPolicy",
      "applicableCountry": "EG",
      "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
      "merchantReturnDays": 14,
      "returnMethod": "https://schema.org/ReturnByMail",
      "returnFees": "https://schema.org/FreeReturn"
    };
    
    // تفاصيل الشحن
    const shippingDetails = {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": "0",
        "currency": "EGP"
      },
      "shippingDestination": {
        "@type": "DefinedRegion",
        "addressCountry": "EG"
      },
      "deliveryTime": {
        "@type": "ShippingDeliveryTime",
        "handlingTime": {
          "@type": "QuantitativeValue",
          "minValue": "1",
          "maxValue": "2",
          "unitCode": "DAY"
        },
        "transitTime": {
          "@type": "QuantitativeValue",
          "minValue": "2",
          "maxValue": "5",
          "unitCode": "DAY"
        }
      }
    };
    
    // التقييمات الحقيقية أو الافتراضية
    let reviews = [];
    if (product.product.reviews && product.product.reviews.length > 0) {
      reviews = product.product.reviews.slice(0, 3).map(review => ({
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": review.rating.toString(),
          "bestRating": "5",
          "worstRating": "1"
        },
        "author": {
          "@type": "Person",
          "name": review.user || "عميل"
        },
        "reviewBody": review.comment || "منتج جيد",
        "datePublished": review.created_at || new Date().toISOString()
      }));
    } else {
      // تقييمات افتراضية
      reviews = [{
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "4.5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "author": {
          "@type": "Person",
          "name": "عميل"
        },
        "reviewBody": "منتج ممتاز وتوصيل سريع",
        "datePublished": new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      }];
    }
    
    // التقييم التجميعي
    const aggregateRating = product.product.average_rating ? {
      "@type": "AggregateRating",
      "ratingValue": product.product.average_rating.toString(),
      "reviewCount": product.product.reviews?.length?.toString() || reviews.length.toString(),
      "bestRating": "5",
      "worstRating": "1"
    } : {
      "@type": "AggregateRating",
      "ratingValue": "4.0",
      "reviewCount": reviews.length.toString(),
      "bestRating": "5",
      "worstRating": "1"
    };
    
    // إنشاء الـ Schema
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.product.name,
      "description": productDescription,
      "sku": product.product.sku || `SKU-${product.product.id}`,
      "mpn": product.product.sku || `MPN-${product.product.id}`,
      // "gtin": gtin, // أزلته لأنه يسبب تحذيراً
      "image": productImages,
      
      // ✅ إصلاح: العلامة التجارية هي اسم المتجر الفعلي
      "brand": {
        "@type": "Brand",
        "name": storeName,
        "logo": storeLogo
      },
      
      // التقييمات
      "aggregateRating": aggregateRating,
      "review": reviews,
      
      // العرض
      "offers": {
        "@type": "Offer",
        "url": `${origin}/product/${product.product.id}`,
        "price": product.price.toString(),
        "priceCurrency": "EGP",
        "priceValidUntil": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "availability": availability,
        "itemCondition": "https://schema.org/NewCondition",
        "seller": {
          "@type": "Store",
          "name": storeName,
          "url": storeWebsite,
          "telephone": storePhone,
          "email": storeEmail,
          "image": storeImage, // ✅ إضافة صورة المتجر
          "logo": storeLogo,
          "address": {
            "@type": "PostalAddress",
            "streetAddress":  "مصر",
            "addressLocality":  "القاهرة",
            "addressRegion": "القاهرة",
            "postalCode": "11511",
            "addressCountry": "EG"
          },
          "priceRange": "$$",
          "paymentAccepted": ["نقداً عند الاستلام", "بطاقة ائتمان"]
        },
        "hasMerchantReturnPolicy": returnPolicy,
        "shippingDetails": shippingDetails,
        "eligibleQuantity": {
          "@type": "QuantitativeValue",
          "minValue": "1",
          "maxValue": product.stock > 0 ? product.stock.toString() : "1"
        }
      },
      
      // الفئة
      "category": product.product.categories?.[0]?.name || "منتجات عامة"
    };
  };

  return (
    <Helmet>
      {products.map((product, index) => (
        <script key={`product-schema-${product.product.id}-${index}`} type="application/ld+json">
          {JSON.stringify(generateProductSchema(product), null, 2)}
        </script>
      ))}
    </Helmet>
  );
};

export default EnhancedProductsJsonLd;